import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { Linkedin, Mail, Phone, Send, Loader2, MapPin } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";

const LOGO_URL = "/assets/logo.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/backend`;

export default function Footer() {
    const { t, lang } = useLanguage();
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({ name: "", email: "", message: "" });
    const [rgpd, setRgpd] = useState({ communications: false, privacy: false });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = true;
        if (!formData.email) {
            newErrors.email = "Requerido";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Inválido";
        }
        if (!formData.message.trim()) newErrors.message = true;
        if (!rgpd.privacy) newErrors.privacy = true;
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const scrollTo = (id) => {
        if (pathname === "/") {
            document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
        } else {
            navigate(`/#${id}`);
        }
    };


    const handleQuickContact = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        setLoading(true);
        try {
            const res = await fetch(`${API}/c_4b9e3d.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...formData, rgpd_privacy: rgpd.privacy, rgpd_communications: rgpd.communications }),
            });
            if (res.ok) {
                toast.success(t.footerForm.success);
                setFormData({ name: "", email: "", message: "" });
                setRgpd({ communications: false, privacy: false });
            } else {
                toast.error(t.footerForm.error);
            }
        } catch {
            toast.error(t.footerForm.error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer id="contacto" className="bg-[#0A1628] text-white py-16 px-6" data-testid="footer">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                    <div>
                        <div className="bg-white rounded-lg p-2.5 inline-block mb-6">
                            <img src={LOGO_URL} alt="NM Global Translations" className="h-14 w-auto" loading="lazy" />
                        </div>
                        <p className="text-slate-400 leading-relaxed text-sm">
                            {t.footer.tagline}
                        </p>
                    </div>

                    <div>
                        <h4 className="font-semibold mb-5 text-sm uppercase tracking-wider">
                            {t.footer.quickLinks}
                        </h4>
                        <ul className="space-y-3">
                            {[
                                { label: t.nav.services, id: "servicios" },
                                { label: t.nav.quote, id: "presupuesto" },
                                { label: t.nav.languages, id: "idiomas" },
                                { label: t.nav.about, id: "sobre-nosotros" },
                                { label: t.nav.payment, id: "formas-de-pago" },
                                { label: t.nav.contact, id: "contacto" },
                            ].map((link) => (
                                <li key={link.id}>
                                    <button
                                        onClick={() => scrollTo(link.id)}
                                        className="text-slate-400 hover:text-white transition-colors duration-200 text-sm"
                                        data-testid={`footer-link-${link.id}`}
                                    >
                                        {link.label}
                                    </button>
                                </li>
                            ))}
                        </ul>

                        <h4 className="font-semibold mt-8 mb-3 text-sm uppercase tracking-wider">
                            {t.footer.followUs}
                        </h4>
                        <div className="flex gap-3">
                            <a
                                href="https://www.linkedin.com/company/nm-global-translations/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-blue-600 transition-colors duration-200"
                                data-testid="social-linkedin"
                                aria-label="LinkedIn"
                            >
                                <Linkedin className="w-5 h-5" />
                            </a>
                        </div>
                    </div>

                    <div>
                        <h4 className="font-semibold mb-5 text-sm uppercase tracking-wider">
                            {t.footer.contact}
                        </h4>
                        <div className="space-y-3">
                            <a
                                href={`mailto:${t.topbar.email}`}
                                className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors duration-200 text-sm"
                                data-testid="footer-email"
                            >
                                <Mail className="w-4 h-4 flex-shrink-0" />
                                {t.topbar.email}
                            </a>
                            <a
                                href={`tel:${t.topbar.phone1.replace(/\s/g, "")}`}
                                className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors duration-200 text-sm"
                                data-testid="footer-phone1"
                            >
                                <Phone className="w-4 h-4 flex-shrink-0" />
                                {t.topbar.phone1}
                            </a>
                            <a
                                href={`tel:${t.topbar.phone2.replace(/\s/g, "")}`}
                                className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors duration-200 text-sm"
                                data-testid="footer-phone2"
                            >
                                <Phone className="w-4 h-4 flex-shrink-0" />
                                {t.topbar.phone2}
                            </a>
                            <div className="flex items-center gap-2 text-slate-400 text-sm mt-1">
                                <MapPin className="w-4 h-4 flex-shrink-0" />
                                {t.footer.address}
                            </div>
                        </div>
                    </div>

                    <div>
                        <h4 className="font-semibold mb-5 text-sm uppercase tracking-wider">
                            {t.footerForm.title}
                        </h4>
                        <form onSubmit={handleQuickContact} className="space-y-3" data-testid="footer-contact-form">
                            <Input
                                placeholder={t.footerForm.name}
                                value={formData.name}
                                onChange={(e) => { setFormData((p) => ({ ...p, name: e.target.value })); setErrors(p => ({...p, name: null})) }}
                                className={`bg-white/10 text-white placeholder:text-slate-500 h-9 text-sm ${errors.name ? 'border-red-500' : 'border-white/10'}`}
                                data-testid="footer-name-input"
                            />
                            <Input
                                type="email"
                                placeholder={t.footerForm.email}
                                value={formData.email}
                                onChange={(e) => { setFormData((p) => ({ ...p, email: e.target.value })); setErrors(p => ({...p, email: null})) }}
                                className={`bg-white/10 text-white placeholder:text-slate-500 h-9 text-sm ${errors.email ? 'border-red-500' : 'border-white/10'}`}
                                data-testid="footer-email-input"
                            />
                            <textarea
                                placeholder={t.footerForm.message}
                                value={formData.message}
                                onChange={(e) => { setFormData((p) => ({ ...p, message: e.target.value })); setErrors(p => ({...p, message: null})) }}
                                rows={2}
                                className={`w-full rounded-md bg-white/10 text-white placeholder:text-slate-500 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 ${errors.message ? 'border-red-500 focus:ring-red-500' : 'border-white/10 focus:ring-blue-500'}`}
                                data-testid="footer-message-input"
                            />
                            <div className="space-y-3 py-2">
                                <div className={`flex items-start gap-2 text-xs ${errors.privacy ? 'text-red-400' : 'text-slate-400'}`}>
                                    <input
                                        type="checkbox"
                                        id="f-privacy"
                                        checked={rgpd.privacy}
                                        onChange={(e) => { setRgpd(p => ({ ...p, privacy: e.target.checked })); setErrors(p => ({...p, privacy: null})) }}
                                        className="mt-0.5 flex-shrink-0 cursor-pointer"
                                    />
                                <label htmlFor="f-privacy" className="cursor-pointer leading-tight">
                                {lang === "es" ? "He leído y acepto la " : "I have read and accept the "}
                                <Link
                                    to={lang === "es" ? "/politica-de-privacidad" : "/privacy-policy"}
                                    className="underline hover:text-blue-400 transition-colors"
                                >
                                    {lang === "es" ? "política de privacidad" : "privacy policy"}
                                </Link>


                                    {" *"}
                                </label>
                                </div>
                                <div className="flex items-start gap-2 text-xs text-slate-400">
                                    <input
                                        type="checkbox"
                                        id="f-comms"
                                        checked={rgpd.communications}
                                        onChange={(e) => setRgpd(p => ({ ...p, communications: e.target.checked }))}
                                        className="mt-0.5 flex-shrink-0 cursor-pointer"
                                    />
                                    <label htmlFor="f-comms" className="cursor-pointer leading-tight">{t.rgpd.communications}</label>
                                </div>
                            </div>
                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-9 text-sm font-medium"
                                data-testid="footer-submit-btn"
                            >
                                {loading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <span className="flex items-center gap-2">
                                        <Send className="w-3.5 h-3.5" />
                                        {t.footerForm.submit}
                                    </span>
                                )}
                            </Button>
                        </form>
                    </div>
                </div>

                <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-center">
                    <p className="text-slate-500 text-sm">
                        &copy; {new Date().getFullYear()} {t.footer.copyright}. {t.footer.rights}
                    </p>
                    <div className="flex gap-5 text-xs text-slate-500">
                        <Link
                            to={lang === "es" ? "/aviso-legal" : "/legal-notice"}
                            className="hover:text-slate-300 transition-colors"
                        >
                            {lang === "es" ? "Aviso Legal" : "Legal Notice"}
                        </Link>
                        <Link
                            to={lang === "es" ? "/politica-de-privacidad" : "/privacy-policy"}
                            className="hover:text-slate-300 transition-colors"
                        >
                            {lang === "es" ? "Política de privacidad" : "Privacy Policy"}
                        </Link>
                        <Link
                            to={lang === "es" ? "/politica-de-cookies" : "/cookie-policy"}
                            className="hover:text-slate-300 transition-colors"
                        >
                            {lang === "es" ? "Política de cookies" : "Cookie Policy"}
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
